import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useV2Auth } from '@/context/V2AuthContext';
import {
  FocusedJourneyLayout, MaskContainer, MaskContext, MaskHeadline,
  MaskInput, MaskTextarea, PrimaryAction, SecondaryAction, MaskBack, CharCounter,
} from '@/components/mask/MaskSystem';
import { SignaturePad } from '@/components/SignaturePad';

/* MOMENTS 1.1 — Teilnehmer: Schreiben → Unterschreiben (Name oder von Hand), Vertrauenssatz, Bearbeiten bis Freigabe */

export default function MomentInviteV2() {
  const { API_V2 } = useV2Auth();
  const { token } = useParams();
  const [params] = useSearchParams();
  const pToken = params.get('p');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const storageKey = `cc_mc_${token}_${pToken || 'open'}`;
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('t01');
  const [text, setText] = useState('');
  const [name, setName] = useState('');
  const [signature, setSignature] = useState(null);
  const [signMode, setSignMode] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    axios.get(`${API_V2}/moments/invite/${token}${pToken ? `?p=${pToken}` : ''}`)
      .then((res) => {
        setInfo(res.data);
        if (res.data.participantName) setName(res.data.participantName);
        const et = localStorage.getItem(storageKey);
        if (et) {
          axios.get(`${API_V2}/moments/invite/${token}/contribution?editToken=${et}`)
            .then((r) => {
              setExisting((prev) => prev || { ...r.data, editToken: et });
              setStep((s) => (s === 't01' ? 'returning' : s));
            })
            .catch(() => localStorage.removeItem(storageKey));
        }
      })
      .catch((err) => setError(err.response?.data?.detail || t('v2.invite.notFound')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_V2, token, pToken]);

  const send = async () => {
    if (!name.trim() || sending) return;
    setSending(true); setSendError(null);
    try {
      if (existing) {
        await axios.put(`${API_V2}/moments/invite/${token}/contribution`, { editToken: existing.editToken, name: name.trim(), message: text.trim(), signature });
      } else {
        const res = await axios.post(`${API_V2}/moments/invite/${token}/contribute`, { name: name.trim(), message: text.trim(), signature, participantToken: pToken });
        localStorage.setItem(storageKey, res.data.editToken);
        setExisting({ editToken: res.data.editToken });
      }
      setStep('t03');
    } catch (err) {
      setSendError(err.response?.data?.detail || t('v2.common.error'));
    } finally { setSending(false); }
  };

  const startEdit = () => {
    setText(existing.message || ''); setName(existing.name || name); setSignature(existing.signature || null);
    setSignMode(!!existing.signature); setStep('write');
  };

  if (error) {
    return (
      <FocusedJourneyLayout>
        <MaskContainer maskKey="err" bgWord={t('v2.bgWords.moment')} align="center" bgShift="-8%">
          <MaskHeadline>{t('v2.shared.gone')}</MaskHeadline>
          <p className="mask-enter mask-enter-delay-2 text-base text-[var(--muted)] mb-10">{error}</p>
          <SecondaryAction onClick={() => navigate('/')} data-testid="invite-error-home">{t('v2.common.whatIs')}</SecondaryAction>
        </MaskContainer>
      </FocusedJourneyLayout>
    );
  }
  if (!info) return null;
  const firstName = info.recipientName.split(' ')[0];
  const initiatorFirst = (info.initiatorName || t('v2.invite.initiatorFallback')).split(' ')[0];
  const trust = t('v2.invite.trust', { initiator: initiatorFirst, name: firstName });

  return (
    <FocusedJourneyLayout>
      {step === 'write' && !existing && <MaskBack onClick={() => setStep('t01')} data-testid="invite-back-btn" />}
      {step === 'name' && <MaskBack onClick={() => setStep('write')} data-testid="invite-back-btn2" />}

      {step === 'returning' && (
        <MaskContainer maskKey="ret" bgWord={t('v2.bgWords.fuerDich')} align="center" bgShift="-8%" data-testid="invite-returning">
          <MaskHeadline>{t('v2.invite.alreadyLeft', { name: firstName })}</MaskHeadline>
          {info.closed || existing?.released ? (
            <p className="mask-enter mask-enter-delay-2 text-base text-[var(--muted)] mb-6" data-testid="invite-frozen">
              {t('v2.invite.frozen', { name: firstName })}
            </p>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-3">
              <PrimaryAction onClick={() => setStep('readown')} data-testid="invite-read-btn">{t('v2.invite.readAgain')}</PrimaryAction>
              <SecondaryAction onClick={startEdit} data-testid="invite-edit-btn">{t('v2.common.edit')}</SecondaryAction>
            </div>
          )}
        </MaskContainer>
      )}

      {step === 'readown' && (
        <MaskContainer maskKey="ro" bgWord={t('v2.bgWords.vonDir')} bgPosition="top" bgShift="12%" data-testid="invite-readown">
          <MaskContext>{t('v2.common.for', { name: firstName })}</MaskContext>
          <p className="mask-enter mask-letter text-xl text-[var(--text)] mb-8" data-testid="invite-own-message">{existing?.message}</p>
          {existing?.signature && <img src={existing.signature} alt={t('v2.invite.signatureAlt')} className="h-16 mb-8" data-testid="invite-own-signature" />}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <SecondaryAction onClick={startEdit} data-testid="invite-edit-btn2">{t('v2.common.edit')}</SecondaryAction>
            <SecondaryAction onClick={() => navigate('/')} data-testid="invite-readown-done">{t('v2.invite.done')}</SecondaryAction>
          </div>
        </MaskContainer>
      )}

      {step === 't01' && (
        <MaskContainer maskKey="t01" bgWord={t('v2.bgWords.fuerName', { name: firstName.toUpperCase() })} align="center" bgShift="-8%" data-testid="invite-t01">
          <MaskContext>{info.occasion}</MaskContext>
          <MaskHeadline>{info.initiatorName ? t('v2.invite.collecting', { initiator: initiatorFirst, name: firstName }) : t('v2.invite.justFor', { name: firstName })}</MaskHeadline>
          {info.closed ? (
            <p className="mask-enter mask-enter-delay-2 text-base text-[var(--muted)] mb-6" data-testid="invite-closed">
              {t('v2.invite.closed')}
            </p>
          ) : (
            <PrimaryAction onClick={() => setStep('write')} data-testid="invite-start-btn">{t('v2.invite.leave')}</PrimaryAction>
          )}
        </MaskContainer>
      )}

      {step === 'write' && (
        <MaskContainer maskKey="t02" bgWord={t('v2.bgWords.aussprechen')} bgPosition="bottom" bgShift="-14%" data-testid="invite-t02">
          <MaskContext>{t('v2.common.for', { name: firstName })}</MaskContext>
          <MaskHeadline>{t('v2.give.headline2', { name: firstName })}</MaskHeadline>
          <p className="mask-enter mask-enter-delay-2 text-sm text-[var(--muted)] mb-8" data-testid="invite-subline2">{t('v2.give.subline2')}</p>
          <MaskTextarea
            autoFocus value={text}
            onChange={(e) => setText(e.target.value.slice(0, 10000))}
            placeholder={t('v2.give.msgPlaceholder')}
            data-testid="invite-message-textarea"
          />
          <CharCounter value={text} max={10000} />
          <p className="text-xs text-[var(--muted)] mt-4 max-w-md" data-testid="invite-trust-note">{trust}</p>
          <div className="mt-8">
            <PrimaryAction onClick={() => setStep('name')} disabled={!text.trim()} data-testid="invite-next-btn">{t('v2.give.send')}</PrimaryAction>
          </div>
        </MaskContainer>
      )}

      {step === 'name' && (
        <MaskContainer maskKey="tname" bgWord={t('v2.bgWords.vonDir')} bgPosition="top" bgShift="14%" data-testid="invite-name-step">
          <MaskContext>{t('v2.common.for', { name: firstName })}</MaskContext>
          <MaskHeadline>{t('v2.invite.signHeadline')}</MaskHeadline>
          <MaskInput
            autoFocus={!signMode} value={name}
            onChange={(e) => setName(e.target.value.slice(0, 60))}
            placeholder={t('v2.give.yourName')} data-testid="invite-name-input"
          />
          <div className="mt-6">
            {!signMode ? (
              <button type="button" onClick={() => setSignMode(true)} data-testid="invite-sign-toggle"
                className="text-sm text-[var(--muted)] hover:text-[var(--text)] py-2 underline underline-offset-4 decoration-[var(--muted)]/40 transition-colors">
                {t('v2.invite.signByHand')}
              </button>
            ) : (
              <div className="mt-2">
                <p className="text-xs uppercase tracking-[0.15em] text-[var(--muted)] mb-3">{t('v2.invite.yourSignature')}</p>
                <SignaturePad value={signature} onChange={setSignature} />
                <button type="button" onClick={() => { setSignMode(false); setSignature(null); }} data-testid="invite-sign-remove"
                  className="mt-2 text-xs text-[var(--muted)] hover:text-[var(--text)] py-1 transition-colors">
                  {t('v2.invite.nameOnly')}
                </button>
              </div>
            )}
          </div>
          <p className="text-xs text-[var(--muted)] mt-6 max-w-md">{trust}</p>
          {sendError && <p className="text-sm text-red-700/80 mt-4" data-testid="invite-error">{sendError}</p>}
          <div className="mt-8">
            <PrimaryAction onClick={send} disabled={!name.trim() || sending} data-testid="invite-send-btn">
              {sending ? t('v2.common.loading') : t('v2.invite.leaveFor', { name: firstName })}
            </PrimaryAction>
          </div>
        </MaskContainer>
      )}

      {step === 't03' && (
        <MaskContainer maskKey="t03" bgWord={t('v2.bgWords.hinterlassen')} align="center" bgShift="-10%" data-testid="invite-t03">
          <MaskHeadline>{t('v2.invite.partOf', { name: firstName })}</MaskHeadline>
          <p className="mask-enter mask-enter-delay-2 text-base text-[var(--muted)] leading-relaxed mb-10 max-w-md mx-auto">
            {t('v2.shared.passOnHeadline')}
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-3">
            <PrimaryAction onClick={() => navigate('/give')} data-testid="invite-ripple-btn">{t('v2.shared.tellSomeone')}</PrimaryAction>
            <SecondaryAction onClick={() => navigate('/')} data-testid="invite-done-btn">{t('v2.common.toHome')}</SecondaryAction>
          </div>
        </MaskContainer>
      )}
    </FocusedJourneyLayout>
  );
}
